import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

export default function Section8ReportPreview({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-24 right-20 w-72 h-72 rounded-full bg-gold-100/6 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-80 h-80 rounded-full bg-primary-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-10 md:mb-12">
          <span
            className={`inline-block text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            结果预览
          </span>
          <h2
            className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            先看一个<span className="text-primary-600">评课结果</span>的样子
          </h2>
          <p
            className={`text-base md:text-lg text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            系统不会只给一段"这节课较好、建议加强互动"的泛泛点评，而是输出一个结构化报告：综合总分、维度分、分档结果、关键证据、改进建议。
          </p>
        </div>

        {/* 按钮入口 */}
        <div
          className={`max-w-3xl mx-auto transition-all duration-1000 delay-300 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-center">
            {/* 渐变背景 */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-background-50 to-gold-50 border border-background-200/60 rounded-3xl" />
            <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-background-200/40" />

            {/* 装饰光晕 */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-primary-200/20 blur-[60px] rounded-full pointer-events-none" />

            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-chart-line text-primary-600 text-2xl" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                AI评课结构化报告
              </h3>
              <p className="text-sm md:text-base text-foreground-500 font-body mb-8 max-w-md mx-auto leading-relaxed">
                包含综合总分、多维度评分、分档结果、关键证据链与改进建议的完整评价输出
              </p>

              <a
                href="#"
                className="inline-flex items-center gap-3 px-10 py-4 rounded-xl gold-shimmer text-white font-body font-bold text-base shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <i className="ri-file-pdf-line text-lg" />
                教育部提升行动报告第二版
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>

          <p className="text-sm text-foreground-400 font-body italic text-center mt-8">
            这里先不展开演示，只建立一个印象：AI评课最终不是一段文字，而是一份能解释、能回看、能汇总的评价报告。
          </p>
        </div>
      </div>
    </div>
  );
}